import { Injectable, inject } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  CreateResourceResponse,
  HttpParams,
} from '@web/common';
import { map, Observable } from 'rxjs';
import { WarehouseRestockCollectionItemResponse } from '../responses/warehouse-restock-collection-item.response';
import { WarehouseRestockCreateRequest } from '../requests/warehouse-restock-create.request';
import { WarehouseRestockUpdateRequest } from '../requests/warehouse-restock-update.request';
import { blank, Guid } from '@web/utils';
import { WarehouseRestockDetailsResponse } from '../responses/warehouse-restock-details.response';
import { WarehouseRestockProductCollectionItemResponse } from '../responses/warehouse-restock-product-collection-item.response';

@Injectable({
  providedIn: 'root',
})
export class WarehouseRestockApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  /* prettier-ignore */
  public fetchCollection(request: CollectionRequest): Observable<CollectionResponse<WarehouseRestockCollectionItemResponse>> {
    const path = 'admin/restock';
    const url = this.provider.httpUrlFactory.withQueryParams(this.hostUrl(), path, this.provider.httpHelper.collectionRequestToQueryParams(request));
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(map(this.provider.httpHelper.bodyToCollectionResponse(WarehouseRestockCollectionItemResponse)));
  }

  /* prettier-ignore */
  public fetchDetailsById(id: Guid): Observable<WarehouseRestockDetailsResponse> {
    const path = `admin/restock/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {[HttpParams.Id]: id});
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(map(this.provider.httpHelper.bodyToResponse(WarehouseRestockDetailsResponse)));
  }

  /* prettier-ignore */
  public create(
    request: WarehouseRestockCreateRequest,
  ): Observable<CreateResourceResponse> {
    const path = 'admin/restock';
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const body = this.provider.httpHelper.requestToBody(request, WarehouseRestockCreateRequest);
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .post(url, body, { context })
      .pipe(map(this.provider.httpHelper.bodyToResponse(CreateResourceResponse)));
  }

  /* prettier-ignore */
  public update(id: Guid, request: WarehouseRestockUpdateRequest): Observable<void> {
    const path = `admin/restock/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });
    const body = this.provider.httpHelper.requestToBody(request, WarehouseRestockUpdateRequest);
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  public fetchProductCollection(
    id: Guid,
    request: CollectionRequest,
  ): Observable<
    CollectionResponse<WarehouseRestockProductCollectionItemResponse>
  > {
    const path = `admin/restock/${HttpParams.Id}/good`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .path(path)
        .host(this.hostUrl())
        .params({ [HttpParams.Id]: id })
        .queryParams(
          this.provider.httpHelper.collectionRequestToQueryParams(request),
        )
        .build(),
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            WarehouseRestockProductCollectionItemResponse,
          ),
        ),
      );
  }

  public approveById(id: Guid): Observable<void> {
    const path = `admin/restock/${HttpParams.Id}/approve`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder.build();
    const body = this.provider.httpHelper.emptyBody();

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  /* prettier-ignore */
  public rejectById(id: Guid): Observable<void> {
    const path = `admin/restock/${HttpParams.Id}/reject`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });
    const context = this.provider.httpContextBuilder.build();
    const body = this.provider.httpHelper.emptyBody();

    return this.provider.http
      .put(url, body,{ context })
      .pipe(blank());
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Warehouse;
  }
}
